import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader, EmptyState } from "@/components/Layout";
import { ClientPanel, type ClientFormValue } from "@/components/forms";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date } from "@/lib/format";
import { useClients, useRefreshDirectory } from "@/lib/queries";
import type { ClientListItem } from "@/lib/types";

export function Clients() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const clients = useClients();
  const [panel, setPanel] = useState<{ open: boolean; client: ClientFormValue | null }>({ open: false, client: null });

  const openNew = () => setPanel({ open: true, client: null });

  return (
    <>
      <PageHeader
        title="Clienti"
        subtitle="Anagrafica, conti, e il proxy e il CID con cui escono"
        actions={canWrite && <Button variant="primary" onClick={openNew}>Nuovo cliente</Button>}
      />

      {clients.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : clients.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(clients.error)}</ErrorNote>
        </div>
      ) : !clients.data?.length ? (
        <EmptyState
          title="Nessun cliente"
          hint="Crea il primo cliente col suo proxy e il suo CID, poi aggiungi i suoi due conti MT5: uno prop e uno broker. Proxy e CID stanno sul cliente, e valgono per tutti i suoi conti."
          action={canWrite && <Button variant="primary" onClick={openNew}>Nuovo cliente</Button>}
        />
      ) : (
        <div className="overflow-x-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="py-2 pr-4 font-medium">Nome</th>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Conti</th>
                <th className="py-2 pr-4 font-medium">Uscita</th>
                <th className="py-2 pr-4 font-medium">Connessione</th>
                <th className="py-2 pr-4 font-medium">Creato</th>
                <th className="py-2 font-medium">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {clients.data.map((c) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  canWrite={canWrite}
                  onOpen={() => navigate(`/clienti/${c.id}`)}
                  onEdit={() => setPanel({ open: true, client: c })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ClientPanel
        open={panel.open}
        client={panel.client}
        onClose={() => setPanel({ open: false, client: null })}
        onSaved={(id) => !panel.client && navigate(`/clienti/${id}`)}
      />
    </>
  );
}

function ClientRow({
  client,
  canWrite,
  onOpen,
  onEdit,
}: {
  client: ClientListItem;
  canWrite: boolean;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const props = client.accounts.filter((a) => a.role === "PROP").length;
  const brokers = client.accounts.filter((a) => a.role === "BROKER").length;
  const connectable = client.accounts.filter((a) => a.role !== "NONE");
  const connected = client.accounts.filter((a) => a.isConnected).length;

  /** In sequenza, non in parallelo: Mirror apre una sessione MT5 per conto. */
  const bulk = async (kind: "connect" | "disconnect") => {
    setBusy(kind);
    setMessage(null);
    const targets = connectable.filter((a) => (kind === "connect" ? !a.isConnected : a.isConnected));
    const failures: string[] = [];
    for (const a of targets) {
      try {
        await (kind === "connect" ? api.accounts.connect(a.id) : api.accounts.disconnect(a.id));
      } catch (e) {
        failures.push(`${a.label}: ${errorMessage(e)}`);
      }
    }
    await refresh();
    setBusy(null);
    if (!targets.length) setMessage(kind === "connect" ? "Tutti i conti con un ruolo sono già connessi." : "Nessun conto connesso.");
    else if (failures.length) setMessage(failures.join(" · "));
  };

  const archive = async () => {
    setBusy("archive");
    setMessage(null);
    try {
      await api.clients.archive(client.id);
      await refresh();
    } catch (e) {
      setMessage(errorMessage(e));
      setBusy(null);
      setConfirmArchive(false);
    }
  };

  return (
    <>
      <tr className="border-b border-line/60 hover:bg-raised/40">
        <td className="py-2.5 pr-4">
          <Link to={`/clienti/${client.id}`} className="font-medium text-ink hover:underline">
            {client.name}
          </Link>
        </td>
        <td className="py-2.5 pr-4 text-muted">{client.email}</td>
        <td className="py-2.5 pr-4">
          {client.accounts.length === 0 ? (
            <span className="text-faint">Nessun conto</span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="text-ink">{client.accounts.length}</span>
              <span className="text-faint">·</span>
              <span className="text-prop">{props} prop</span>
              <span className="text-faint">·</span>
              <span className="text-broker">{brokers} broker</span>
            </span>
          )}
        </td>
        {/* Da dove esce: paese e citta' si leggono senza svelare la password,
            perche' non ci stanno piu' dentro. */}
        <td className="py-2.5 pr-4">
          {client.defaultProxy ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={`h-2 w-2 rounded-full ${client.defaultProxy.lastCheckOk === null ? "bg-faint" : client.defaultProxy.lastCheckOk ? "bg-gain" : "bg-loss"}`}
                title={client.defaultProxy.lastCheckAt ? `Ultimo controllo ${date(client.defaultProxy.lastCheckAt)}` : "Mai controllato"}
                aria-hidden
              />
              <span className="text-ink">
                {[client.defaultProxy.city, client.defaultProxy.country].filter(Boolean).join(", ") || `${client.defaultProxy.host}:${client.defaultProxy.port}`}
              </span>
            </span>
          ) : (
            <span className="text-warn" title="Senza proxy il conto prop non si connette">
              Nessun proxy
            </span>
          )}
        </td>
        <td className="py-2.5 pr-4">
          {client.accounts.length === 0 ? (
            <span className="text-faint">—</span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={`h-2 w-2 rounded-full ${connected === 0 ? "bg-faint" : connected === client.accounts.length ? "bg-gain" : "bg-warn"}`}
                aria-hidden
              />
              <span className={connected ? "text-ink" : "text-muted"}>
                {connected} di {client.accounts.length} connessi
              </span>
            </span>
          )}
        </td>
        <td className="py-2.5 pr-4 text-muted">{date(client.createdAt)}</td>
        <td className="py-2.5">
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onOpen}>
              Apri
            </Button>
            {canWrite && (
              <>
                <Button size="sm" variant="ghost" onClick={onEdit}>
                  Modifica
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy || connectable.length === 0} onClick={() => void bulk("connect")}>
                  {busy === "connect" ? "Connessione…" : "Connetti tutti"}
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy || connected === 0} onClick={() => void bulk("disconnect")}>
                  {busy === "disconnect" ? "Disconnessione…" : "Disconnetti tutti"}
                </Button>
                {confirmArchive ? (
                  <>
                    <Button size="sm" variant="danger" disabled={!!busy} onClick={() => void archive()}>
                      {busy === "archive" ? "Archiviazione…" : "Conferma archiviazione"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmArchive(false)}>
                      Annulla
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setConfirmArchive(true)}>
                    Archivia
                  </Button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {message && (
        <tr className="border-b border-line/60">
          <td colSpan={7} className="pb-2.5 text-xs text-loss">
            {message}
          </td>
        </tr>
      )}
    </>
  );
}
